import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { client } from '../../api/client';

interface PendingField {
  key: string;
  value: string;
  needsEn: boolean;
  needsZhCn: boolean;
}

interface PendingItem {
  id: number;
  label: unknown;
  fields: PendingField[];
}

interface PendingData {
  [table: string]: PendingItem[];
}

const PROMPT_TEMPLATE = `你現在是高端度假租賃品牌「HK Stay Islands」的專業翻譯助手。

請將下方 JSON 中每個 field 的繁體中文內容（value），翻譯成：
1. 简体中文（zh_cn）
2. 英文（en）

翻譯指引：
- 品牌名稱「HK Stay Islands」保留英文，不要翻譯。
- 住宿名稱以 "Stay" 開頭時（例如 "Stay Mikado 御海閣"），"Stay" 保留英文；後面中文名稱請翻成自然簡體中文，英文則採用音譯或意譯，讓國際旅客容易理解。
- 語氣高端、簡潔、優雅。
- 不要逐字直譯，要讓目標語言讀者感覺自然。
- 保留原有換行、HTML 標籤、Markdown 格式。
- 如果內容已經是英文，請直接複製到 en；如果已經是簡體，請直接複製到 zh_cn。

輸入是一筆一筆記錄，每筆包含 id、資料表名稱（table）、標籤（label）以及需要翻譯的欄位（fields）。
請只回傳一個 JSON 陣列，結構與輸入相同，並在每個 field 中加入 "en" 與 "zh_cn"。不要加入任何解釋。

輸入：
{{TRANSLATION_JSON}}`;

const TABLE_LABELS: Record<string, string> = {
  properties: '住宿',
  room_types: '房型',
  experiences: '體驗',
  packages: '套餐',
  cms_articles: 'CMS 文章',
  membership_tiers: '會員等級',
};

export default function TranslationsSection() {
  const { t } = useTranslation('admin');
  const [pending, setPending] = useState<PendingData>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [applyJson, setApplyJson] = useState('');
  const [force, setForce] = useState(false);

  async function loadPending() {
    setLoading(true);
    setError('');
    try {
      const res = await client.api.fetch('/api/admin/translations/pending');
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('common.loadFailed'));
      setPending(data.data || {});
    } catch (err: any) {
      setError(err.message || t('translations.loadError'));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadPending();
  }, []);

  function buildPrompt(items: PendingItem[], table: string): string {
    const payload = items.map((item) => ({
      id: item.id,
      table,
      label: item.label,
      fields: item.fields.map((f) => ({
        key: f.key,
        value: f.value,
      })),
    }));
    return PROMPT_TEMPLATE.replace('{{TRANSLATION_JSON}}', JSON.stringify(payload, null, 2));
  }

  async function copyPrompt(table: string) {
    const items = pending[table];
    if (!items || items.length === 0) return;
    try {
      await navigator.clipboard.writeText(buildPrompt(items, table));
      setSuccess(t('translations.promptCopied', { table: TABLE_LABELS[table] || table }));
    } catch {
      setError(t('translations.copyFailed'));
    }
  }

  async function applyTranslations() {
    setError('');
    setSuccess('');
    let parsed: unknown;
    try {
      parsed = JSON.parse(applyJson);
    } catch {
      setError(t('translations.invalidJson'));
      return;
    }

    setLoading(true);
    try {
      const res = await client.api.fetch('/api/admin/translations/apply', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ force, items: parsed }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('common.saveFailed'));
      setApplyJson('');
      setSuccess(t('translations.applySuccess', { updated: data.data?.updated || 0 }));
      await loadPending();
    } catch (err: any) {
      setError(err.message || t('translations.applyError'));
    } finally {
      setLoading(false);
    }
  }

  const totalMissing = Object.values(pending).reduce((sum, items) => sum + items.length, 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-lg text-[#0d1b2a]">{t('translations.title')}</h3>
        <button
          onClick={loadPending}
          disabled={loading}
          className="text-sm text-[#0a4c6b] hover:underline disabled:opacity-50"
        >
          {loading ? t('common.loading') : t('translations.refresh')}
        </button>
      </div>

      {error && <p className="text-sm text-red-600 bg-red-50 px-4 py-3 rounded-lg">{error}</p>}
      {success && <p className="text-sm text-green-600 bg-green-50 px-4 py-3 rounded-lg">{success}</p>}

      {totalMissing === 0 && !loading && (
        <p className="text-sm text-gray-600 bg-gray-50 px-4 py-3 rounded-lg">
          {t('translations.allCaughtUp')}
        </p>
      )}

      {Object.entries(pending).map(([table, items]) => (
        <div key={table} className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b flex items-center justify-between">
            <div>
              <h4 className="font-medium text-[#0d1b2a]">{TABLE_LABELS[table] || table}</h4>
              <p className="text-xs text-gray-500">{t('translations.pendingCount', { count: items.length })}</p>
            </div>
            <button
              onClick={() => copyPrompt(table)}
              className="bg-[#0a4c6b] text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-[#083d56] transition"
            >
              {t('translations.copyPrompt')}
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-2 text-left font-medium text-gray-600">ID</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-600">{t('translations.label')}</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-600">{t('translations.fields')}</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {items.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 font-mono">#{item.id}</td>
                    <td className="px-4 py-2 font-medium text-[#0d1b2a]">{String(item.label)}</td>
                    <td className="px-4 py-2 text-gray-600">
                      {item.fields.map((f) => f.key).join(', ')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
        <h4 className="font-semibold text-[#0d1b2a] mb-3">{t('translations.applyTitle')}</h4>
        <p className="text-sm text-gray-500 mb-3">{t('translations.applyHint')}</p>
        <textarea
          value={applyJson}
          onChange={(e) => setApplyJson(e.target.value)}
          rows={8}
          placeholder={t('translations.applyPlaceholder')}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0a4c6b]/20 focus:border-[#0a4c6b] outline-none mb-3"
        />
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={force}
              onChange={(e) => setForce(e.target.checked)}
              className="rounded border-gray-300 text-[#0a4c6b] focus:ring-[#0a4c6b]"
            />
            {t('translations.forceOverwrite')}
          </label>
          <button
            onClick={applyTranslations}
            disabled={loading || !applyJson.trim()}
            className="bg-[#0a4c6b] text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-[#083d56] transition disabled:opacity-60"
          >
            {loading ? t('common.saving') : t('translations.apply')}
          </button>
        </div>
      </div>
    </div>
  );
}
